import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';

import { BidsService } from '@app/services/bids.service';
import { RestBulkScopeAndBidModel } from '@shared/interfaces/rest-bulk-scope-and-bid-model';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  addBulkTurnoversAttachment,
  addBulkTurnoversAttachmentFailed,
  addBulkTurnoversAttachmentSuccess,
  addUnitsToBulk,
  addUnitsToBulkFailed,
  addUnitsToBulkSuccess,
  createNewBulkScope,
  createNewBulkScopeFailed,
  createNewBulkScopeSuccess,
  getBulkScope,
  getBulkScopeFailed,
  getBulkScopeList,
  getBulkScopeListSuccess,
  getBulkScopeSuccess,
  updateBulkScope,
  updateBulkScopeFailed,
  updateBulkScopeSuccess,
} from '../actions/bulk-and-scope.actions';

@Injectable()
export class BulkBidsEffects {
  createNewBulkScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createNewBulkScope),
      switchMap(action =>
        this.bidsService.createBulkScope(action.bulkScope).pipe(
          mergeMap((bulkScopeId: RestBulkScopeAndBidModel) => [
            createNewBulkScopeSuccess({ bulkScopeId: bulkScopeId.id }),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.CreatingBulkScope);
            return of(createNewBulkScopeFailed());
          })
        )
      )
    )
  );

  getBulkScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getBulkScope),
      switchMap(action =>
        this.bidsService.getBulkScope(action.bulkScopeId).pipe(
          map(bulkScope => getBulkScopeSuccess({ bulkScope })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingBulkScope);
            return of(getBulkScopeFailed());
          })
        )
      )
    )
  );

  updateBulkScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateBulkScope),
      mergeMap(action =>
        this.bidsService.updateBulkScope(action.bulkScope).pipe(
          map(() => updateBulkScopeSuccess()),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingBulkScope);
            return of(updateBulkScopeFailed());
          })
        )
      )
    )
  );

  getBulkScopeList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getBulkScopeList),
      switchMap(action =>
        this.bidsService.getBulkScopeList(action.propertyId).pipe(
          map(list => getBulkScopeListSuccess({ bulkList: list || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingBulkScopeList);
            return of(getBulkScopeFailed());
          })
        )
      )
    )
  );

  addBulkTurnoversAttachment = createEffect(() =>
    this.actions$.pipe(
      ofType(addBulkTurnoversAttachment),
      mergeMap(action =>
        this.bidsService.addBulkTurnoversAttachment(action.attachment).pipe(
          map(() => addBulkTurnoversAttachmentSuccess()),
          catchError(() => of(addBulkTurnoversAttachmentFailed()))
        )
      )
    )
  );

  addUnitsToBulk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addUnitsToBulk),
      mergeMap(action =>
        this.bidsService.addUnitsToBulk(action.bulkScope).pipe(
          map(() => addUnitsToBulkSuccess()),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingBulkScopeList);
            return of(addUnitsToBulkFailed());
          })
        )
      )
    )
  );

  constructor(private actions$: Actions, private bidsService: BidsService, private snackbarService: SnackbarService) {}
}
